/* iter154 — Category hierarchy client cache.
 *
 * Responsibilities:
 *   • Fetch the full L1 + L2 hierarchy from /api/categories and cache it
 *     in localStorage under `habano.cache.categories` (24h TTL) so the
 *     filter sheet + business cards work offline.
 *   • Provide sync helpers to read the cache (`getCachedCategories`,
 *     `getL1`, `getL2ForParent`, `findCategory`, `formatCategoryLabel`).
 *   • Bind Level-1 slugs back to the visual metadata from
 *     `lib/categories.js` (icons, colors) so existing components keep
 *     working without re-plumbing.
 *
 * Additive-only: this module is a NEW read-through cache that layers
 * on top of the existing `lib/categories.js` static list. Nothing in
 * that file is renamed or removed.
 */
import { CATEGORIES as CORE_CATEGORIES, resolveCategory } from "./categories";
import { api } from "./api";

const CACHE_KEY = "habano.cache.categories";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

let _memory = null;      // in-memory copy of last-read cache
let _inflight = null;    // dedupe concurrent fetches

// ── Cache read/write ────────────────────────────────────────────────
export function getCachedCategories() {
  if (_memory) return _memory;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.categories)) return null;
    if (parsed.fetched_at && Date.now() - parsed.fetched_at > CACHE_TTL_MS) {
      // Stale but still usable offline — return it, but let the caller
      // refresh in the background.
    }
    _memory = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function _writeCache(hierarchy, seedVersion) {
  const payload = {
    categories: hierarchy,
    seed_version: seedVersion,
    fetched_at: Date.now(),
  };
  _memory = payload;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch { /* quota — ignore */ }
  return payload;
}

// ── Fetch (deduped) ─────────────────────────────────────────────────
export async function fetchCategories({ force = false } = {}) {
  if (!force) {
    const cached = getCachedCategories();
    if (cached && Date.now() - (cached.fetched_at || 0) < CACHE_TTL_MS) {
      return cached;
    }
  }
  if (_inflight) return _inflight;
  _inflight = api.get("/categories")
    .then(({ data }) => _writeCache(data.categories || [], data.seed_version))
    .catch(() => {
      // Fall back to the static list so the UI never breaks offline.
      const staticHierarchy = CORE_CATEGORIES.map((c, idx) => ({
        id: `static-${c.slug}`,
        slug: c.slug,
        level: 1,
        parent_slug: null,
        name_en: c.label?.en || c.slug,
        name_es: c.label?.es || c.slug,
        icon: "",
        visible: true,
        order: idx,
        children: [],
      }));
      return _writeCache(staticHierarchy, 0);
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Fire-and-forget refresh — used on app boot. */
export function warmCategoryCache() {
  fetchCategories({ force: false }).catch(() => {});
}

// ── Read helpers ────────────────────────────────────────────────────
export function getL1() {
  const c = getCachedCategories();
  if (!c) return [];
  return c.categories.filter((r) => r.visible !== false);
}

export function getL2ForParent(parentSlug) {
  const c = getCachedCategories();
  if (!c) return [];
  const parent = c.categories.find((r) => r.slug === parentSlug);
  if (!parent) return [];
  return (parent.children || []).filter((r) => r.visible !== false);
}

/**
 * Look up a category by slug at any level.
 * @returns {{level: 1|2, slug, parent_slug, name_en, name_es} | null}
 */
export function findCategory(slug, { parentSlug } = {}) {
  const c = getCachedCategories();
  if (!c) return null;
  for (const l1 of c.categories) {
    if (l1.slug === slug && !parentSlug) return l1;
    for (const l2 of (l1.children || [])) {
      if (l2.slug === slug && (!parentSlug || l1.slug === parentSlug)) return l2;
    }
  }
  return null;
}

/**
 * Format the display label for a business as "L1 · L2".
 * Falls back gracefully when subcategory is null/unknown.
 * @param {string} lang "en" | "es"
 */
export function formatCategoryLabel({ category, subcategory }, lang = "en") {
  const l1 = findCategory(category);
  const l1Label = l1 ? (lang === "es" ? l1.name_es : l1.name_en) :
    (resolveCategory(category)?.label?.[lang] || category || "");
  if (!subcategory) return l1Label;
  const l2 = findCategory(subcategory, { parentSlug: category });
  if (!l2) return l1Label;
  const l2Label = lang === "es" ? l2.name_es : l2.name_en;
  return { l1: l1Label, l2: l2Label };
}
